// src/admin/AdminUserPermissions.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

// Define available admin pages
const ADMIN_PAGES = [
  { id: 'journeys', label: 'Manage Journeys', description: 'Create and edit journey content' },
  { id: 'routes', label: 'Manage Routes', description: 'Manage store routes and pricing' },
  { id: 'hostels', label: 'Manage Hostels', description: 'Manage hostel listings' },
  { id: 'tips', label: 'Tips & Feedback', description: 'View and manage user tips and feedback' },
  { id: 'explore-cards', label: 'Manage Explore Cards', description: 'Create and edit explore cards' },
  { id: 'cities', label: 'Manage Cities', description: 'Manage city information' },
  { id: 'users', label: 'Manage Users', description: 'Edit user profile information' }
];

// Super admin emails - these users have access to everything including user permissions
const SUPER_ADMIN_EMAILS = [
  'matthew@backpass.co',
  'mgriff7@gmail.com',
  'mhgbackpass@gmail.com',
  'mhgcommercial@gmail.com'
];

function AdminUserPermissions() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { currentUser } = useAuth();
  
  // Check if current user is super admin
  const isCurrentUserSuperAdmin = SUPER_ADMIN_EMAILS.includes(currentUser?.email?.toLowerCase());
  
  useEffect(() => {
    if (!isCurrentUserSuperAdmin) {
      setError('Access denied. Only super administrators can manage user permissions.');
      setLoading(false);
      return;
    }
    fetchUsers();
  }, [isCurrentUserSuperAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
        adminPermissions: doc.data().adminPermissions || {}
      }));
      
      // Sort users: super admins first, then by email
      usersData.sort((a, b) => {
        const aIsSuper = SUPER_ADMIN_EMAILS.includes(a.email?.toLowerCase());
        const bIsSuper = SUPER_ADMIN_EMAILS.includes(b.email?.toLowerCase());
        if (aIsSuper && !bIsSuper) return -1;
        if (!aIsSuper && bIsSuper) return 1;
        return (a.email || '').localeCompare(b.email || '');
      });
      
      setUsers(usersData);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (userId, pageId) => {
    const user = users.find(u => u.uid === userId);
    if (!user) return;

    // Prevent modifying super admin permissions
    if (SUPER_ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      setError('Cannot modify super admin permissions');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSaving(prev => ({ ...prev, [`${userId}-${pageId}`]: true }));
    setError('');
    setSuccessMessage('');

    try {
      const currentPermissions = user.adminPermissions || {};
      const newPermissions = {
        ...currentPermissions,
        [pageId]: !currentPermissions[pageId]
      };

      // Update Firestore
      await updateDoc(doc(db, 'users', userId), {
        adminPermissions: newPermissions,
        isAdmin: Object.values(newPermissions).some(v => v) // Set isAdmin flag if any permission is true
      });

      // Update local state
      setUsers(prev => prev.map(u => 
        u.uid === userId 
          ? { ...u, adminPermissions: newPermissions }
          : u
      ));

      setSuccessMessage(`Updated permissions for ${user.firstName || user.email}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError(`Failed to update permissions: ${err.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [`${userId}-${pageId}`]: false }));
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.phoneNumber?.includes(searchTerm)
    );
  });

  const hasAnyAdminAccess = (user) => {
    if (SUPER_ADMIN_EMAILS.includes(user.email?.toLowerCase())) return true;
    return user.adminPermissions && Object.values(user.adminPermissions).some(v => v);
  };

  const styles = {
    container: { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
    searchContainer: { marginBottom: '20px' },
    searchInput: {
      width: '100%',
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px'
    },
    table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
    th: {
      border: '1px solid #ddd',
      padding: '12px',
      backgroundColor: '#f8f9fa',
      textAlign: 'left',
      fontWeight: '600',
      fontSize: '14px'
    },
    td: { border: '1px solid #ddd', padding: '12px', fontSize: '14px' },
    userCell: { minWidth: '200px' },
    permissionCell: { textAlign: 'center', width: '120px' },
    checkbox: { cursor: 'pointer', width: '18px', height: '18px' },
    disabledCheckbox: { cursor: 'not-allowed', opacity: 0.5 },
    superAdminBadge: {
      display: 'inline-block',
      marginLeft: '8px',
      padding: '2px 8px',
      backgroundColor: '#007bff',
      color: 'white',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 'bold'
    },
    adminBadge: {
      display: 'inline-block',
      marginLeft: '8px',
      padding: '2px 8px',
      backgroundColor: '#28a745',
      color: 'white',
      borderRadius: '12px',
      fontSize: '11px'
    },
    error: {
      color: '#dc3545',
      backgroundColor: '#f8d7da',
      border: '1px solid #f5c6cb',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    success: {
      color: '#155724',
      backgroundColor: '#d4edda',
      border: '1px solid #c3e6cb',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    infoBox: {
      backgroundColor: '#e7f3ff',
      border: '1px solid #b3d9ff',
      borderRadius: '4px',
      padding: '15px',
      marginBottom: '20px',
      fontSize: '14px'
    },
    accessDenied: {
      textAlign: 'center',
      padding: '50px',
      color: '#dc3545'
    }
  };

  if (!isCurrentUserSuperAdmin && !loading) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>Only super administrators can manage user permissions.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <h1>User Permissions Management</h1>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1>User Permissions Management</h1>
      
      <div style={styles.infoBox}>
        <strong>Instructions:</strong> Grant users access to specific admin pages by checking the boxes below. 
        Users will only see and access the pages you enable for them. Super admins (marked with a blue badge) 
        have access to all pages and cannot be modified. Note: Only super admins can access this permissions page.
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {successMessage && <div style={styles.success}>{successMessage}</div>}

      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>User</th>
            {ADMIN_PAGES.map(page => (
              <th key={page.id} style={{ ...styles.th, ...styles.permissionCell }}>
                {page.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(user => {
            const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(user.email?.toLowerCase());
            
            return (
              <tr key={user.uid}>
                <td style={styles.td}>
                  <div>
                    <strong>{user.firstName} {user.lastName}</strong>
                    {isSuperAdmin && <span style={styles.superAdminBadge}>SUPER ADMIN</span>}
                    {!isSuperAdmin && hasAnyAdminAccess(user) && <span style={styles.adminBadge}>Admin</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {user.email}
                  </div>
                  {user.phoneNumber && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {user.phoneNumber}
                    </div>
                  )}
                </td>
                {ADMIN_PAGES.map(page => (
                  <td key={page.id} style={{ ...styles.td, ...styles.permissionCell }}>
                    <input
                      type="checkbox"
                      checked={isSuperAdmin || (user.adminPermissions && user.adminPermissions[page.id])}
                      onChange={() => handlePermissionToggle(user.uid, page.id)}
                      disabled={isSuperAdmin || saving[`${user.uid}-${page.id}`]}
                      style={isSuperAdmin ? { ...styles.checkbox, ...styles.disabledCheckbox } : styles.checkbox}
                      title={isSuperAdmin ? 'Super admins have all permissions' : `Toggle ${page.label} access`}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredUsers.length === 0 && (
        <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          No users found matching "{searchTerm}"
        </p>
      )}
    </div>
  );
}

export default AdminUserPermissions;